import {identity} from 'lodash';
import React, {ReactNode, useRef} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {
  ClickEventHandler,
  Handler,
  HandlerOf,
  Renderer,
  RendererOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../boxedIcon';
import {getCellBackgroundColor, getCellIndexStyles} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {StyledActionsDiv, StyledActionsIcon} from './settingsCommonCellStyles';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

type ResourceWithName = {name: string};
type ExpandableResourceCellIconNode = {node: ReactNode};
type ExpandableResourceCellIconWithName = {
  name: IconName;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
};

export type ExpandableResourceCellIconProps =
  | ExpandableResourceCellIconNode
  | ExpandableResourceCellIconWithName;

export interface ExpandableResourceCellInheritableProps<R extends ResourceWithName> {
  onChevronClick?: Handler;
  hasChevron: boolean;
  hasHover: boolean;
  isExpanded: boolean;
  renderActionsDropdown: RendererOf<{
    modalProps: PopoverTransferProps;
    value: R | undefined;
  }>;
  onActionsDropdownOpened: HandlerOf<boolean>;
}

interface ExpandableResourceCellProps<R extends ResourceWithName>
  extends TableCellProps<R>,
    ExpandableResourceCellInheritableProps<R> {
  renderSubtitle: RendererOf<{
    seperatorNode: ReactNode;
  }>;
  renderTooltip?: Renderer;
  iconProps: ExpandableResourceCellIconProps;
  shouldHideActions: boolean;
  hasChildren: boolean;
  shouldShowSubtitle: boolean;
  onClick?: ClickEventHandler;
  nestingLevel: number;
}

/*
 * Style.
 */

const StyledCircleSeperatorSpan = styled.span<SelectedStyleProps>`
  display: inline-block;
  border-radius: 100px;
  background-color: ${(p) => (p.$isSelected ? p.theme.foreground : p.theme.greys.shade70)};
  margin: 1px 2px 0px 2px;
  width: 10px;
  height: 10px;
  transform: scale(0.35); // Without this, the "circle" becomes more of a rounded rectangle.
`;

interface NameWrapperStyleProps extends TableCellStyleProps {
  $indentLevel: number;
  $hasChevron: boolean;
}
const StyledNameWrapperDiv = styled.div<NameWrapperStyleProps>`
  display: grid;
  align-items: center;
  flex: 1;
  height: 100%;

  ${(p) => addGridColumnsStyles(p)};
  ${(p) => addPaddingLeftStyles(p)};

  overflow: hidden;
  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

function addGridColumnsStyles(props: NameWrapperStyleProps) {
  if (props.$hasChevron) {
    return css`
      grid-template-areas: '. chevron . highlight . label . actions .';
      grid-template-columns: 12px minmax(26px, auto) 7px auto 12px 1fr 7px auto 8px;
    `;
  }

  return css`
    grid-template-areas: '. highlight . label . actions .';
    grid-template-columns: 12px auto 12px 1fr 7px auto 8px;
  `;
}

function addPaddingLeftStyles({$indentLevel}: NameWrapperStyleProps) {
  const indentMultiplier = Math.min($indentLevel || 0, 3);
  const indentPadding = indentMultiplier * 40;
  return css`
    padding-left: ${indentPadding}px;
  `;
}

const StyledChevronIconWrapper = styled.div`
  grid-area: chevron;
  ${flexCenter};
`;
const StyledHighlightDiv = styled.div`
  grid-area: highlight;
`;

interface TagLabelStyleProps {
  $hasSubtitle?: boolean;
}
const StyledLabelDiv = styled.div<TagLabelStyleProps>`
  grid-area: label;
  overflow: hidden;

  ${(p) => addLabelStyles(Boolean(p.$hasSubtitle))};
`;

function addLabelStyles(hasSubtitle: boolean) {
  if (hasSubtitle) {
    return css`
      display: grid;
      grid-row-gap: 4px;
      grid-auto-flow: row;
      grid-auto-rows: auto;
      align-items: center;
    `;
  }

  return css`
    ${flexCenter};
  `;
}

interface SelectedStyleProps {
  $isSelected?: boolean;
}

const StyledNameDiv = styled.div<SelectedStyleProps>`
  display: flex;
  align-items: center;
  flex: 1;
  color: ${(p) => (p.$isSelected ? p.theme.invertedText : p.theme.greys.shade90)};
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.medium};
  ${ellipsis()};
`;

const StyledSubtitleDiv = styled.div<SelectedStyleProps>`
  color: ${(p) => (p.$isSelected ? p.theme.invertedText : p.theme.greys.shade70)};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
  display: flex;
  min-width: 0;
`;

const StyledSubtitleContentDiv = styled.div`
  ${ellipsis()}
  display: flex;
  align-items: center;
`;

/*
 * Component.
 */

export const ExpandableResourceCell = <R extends ResourceWithName>(props: ExpandableResourceCellProps<R>) => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    nestingLevel,
    hasChildren,
    renderTooltip,
    shouldShowSubtitle,
    shouldHideActions,
  } = props;
  const iconRef = useRef<HTMLDivElement>(null);

  return (
    <TableCell<R>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={() => (
        <StyledNameWrapperDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
          $indentLevel={nestingLevel}
          $hasChevron={props.hasChevron}
          className={buildHoverParentClassName()}
          onClick={(e) => {
            if (e.defaultPrevented || !props.onClick) {
              return;
            }

            props.onClick(e);
          }}
        >
          {props.hasChevron && (
            <StyledChevronIconWrapper>
              {hasChildren && (
                <IconButton
                  label={
                    props.isExpanded ? (
                      <Trans id="settingstable-collapse">Collapse</Trans>
                    ) : (
                      <Trans id="settingstable-expand">Expand</Trans>
                    )
                  }
                  iconName={props.isExpanded ? 'chevronDownSmall' : 'chevronRightSmall'}
                  onClick={(e) => {
                    e.preventDefault();
                    props.onChevronClick?.();
                  }}
                  color={props.isSelected ? theme.greys.white : theme.greys.shade80}
                  hoverColor={props.isSelected ? theme.greys.white : theme.greys.shade80}
                  backgroundHoverColor={(props.isSelected && theme.alphas.white30) || undefined}
                />
              )}
            </StyledChevronIconWrapper>
          )}
          <StyledHighlightDiv>{renderIcon(props.iconProps)}</StyledHighlightDiv>
          <StyledLabelDiv $hasSubtitle={shouldShowSubtitle}>
            <StyledNameDiv $isSelected={props.isSelected}>
              {props.value?.name} {renderTooltip ? renderTooltip() : null}
            </StyledNameDiv>
            {renderSubtitle(props)}
          </StyledLabelDiv>
          {!shouldHideActions && (
            <StyledActionsDiv $isSelected={props.isSelected} $hasHover={props.hasHover}>
              <StyledActionsIcon
                label={<Trans id="settingstable-actions">Actions</Trans>}
                iconName="moreVertical"
                color={props.isSelected ? theme.greys.white : theme.greys.shade70}
                hoverActiveColor={props.isSelected ? theme.greys.white : theme.greys.shade80}
                hoverColor={props.isSelected ? theme.greys.white : theme.greys.shade80}
                isOpen={props.hasHover}
                hasHover={props.hasHover}
                onClick={(e) => {
                  e.preventDefault();
                  props.onActionsDropdownOpened(true);
                }}
                ref={iconRef}
              />
              {props.hasHover &&
                props.renderActionsDropdown({
                  modalProps: {
                    anchor: iconRef,
                    onRequestClose: () => props.onActionsDropdownOpened(false),
                    wrap: identity,
                  },
                  value: props.value,
                })}
            </StyledActionsDiv>
          )}
        </StyledNameWrapperDiv>
      )}
    />
  );
};

function renderSubtitle<R extends ResourceWithName>(props: ExpandableResourceCellProps<R>) {
  return (
    <StyledSubtitleDiv $isSelected={props.isSelected}>
      <StyledSubtitleContentDiv>
        {props.renderSubtitle({
          seperatorNode: <StyledCircleSeperatorSpan $isSelected={props.isSelected} />,
        })}
      </StyledSubtitleContentDiv>
    </StyledSubtitleDiv>
  );
}

function renderIcon(iconProps: ExpandableResourceCellIconProps) {
  if ('node' in iconProps) {
    return iconProps.node;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <BoxedIcon
          name={iconProps.name}
          backgroundColor={iconProps.backgroundColor || theme.foreground}
          color={iconProps.color || theme.greys.shade70}
          borderColor={iconProps.borderColor}
          borderStyle="solid"
          size={VisualSizesEnum.MEDIUM}
        />
      )}
    </UserThemeConsumer>
  );
}
